import { useState } from 'react';
import { api, type CompanyFeature } from '../api/client';
import { shortWhen } from '../lib/format';
import { useData } from '../lib/useData';
import { Button } from './ui';

/**
 * "Ask Stitch to turn this on" (1.13.0, Teampace 3000), wherever a feature is off and only Stitch
 * can turn it on. The company's admins get the button (once asked, it says so); everyone else is
 * told who their admins are, so requests come through them. Nothing shows when the feature is on,
 * or when it's the company's own setting.
 */
export function AskStitch({ tenant, feature, compact }: { tenant: string; feature: string; compact?: boolean }) {
  const data = useData(() => api.companyFeatures(tenant), [tenant], { live: false });
  const f = data.data?.features.find((x) => x.id === feature);
  if (!data.data || !f) return null;
  return <AskFor tenant={tenant} f={f} admin={data.data.admin} admins={data.data.admins} onAsked={data.reload} compact={compact ?? false} />;
}

export function AskFor({ tenant, f, admin, admins, onAsked, compact }: { tenant: string; f: CompanyFeature; admin: boolean; admins: string[]; onAsked: () => void; compact: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (f.state === 'on' || !f.askable) return null;
  const r = f.request;
  if (r?.status === 'open') {
    return (
      <span className="ask-stitch muted" role="status">
        {`Asked ${shortWhen(r.askedAt)} by ${r.askedByName || 'your admin'}. Stitch support will answer.`}
      </span>
    );
  }
  if (!admin) {
    const who = admins.length ? (admins.length <= 2 ? admins.join(' or ') : `${admins.slice(0, 2).join(', ')} or another admin`) : 'your company’s admin';
    return <span className="ask-stitch muted">{`To ask for it, ask ${who}.`}</span>;
  }
  return (
    <span className="ask-stitch">
      {r?.status === 'closed' && !compact && <span className="muted">{`Stitch support answered ${shortWhen(r.answeredAt ?? r.askedAt)}: “${r.reason}”`}</span>}
      <Button
        size="small"
        variant="secondary"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError('');
          try {
            await api.askForFeature(tenant, f.id);
            onAsked();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'That didn’t go through. Try again.');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Asking…' : r?.status === 'closed' ? 'Ask again' : 'Ask Stitch to turn this on'}
      </Button>
      {error && <span className="field-error">{error}</span>}
    </span>
  );
}
